import { useCallback, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { AiRegisterRow, fmtDate } from '@/pages/admin/AiRegisterRow'
import { useAiOwners } from '@/pages/admin/OwnerPicker'
import type { Draft, RegisterFeature } from '@/pages/admin/AiRegisterRow'

// AI-REGISTER-1B — every AI feature, its 30-day use, and who owns it at what
// risk. Owner and risk are the owner's decisions: nothing is defaulted, and
// nothing is sent until that row's Save is pressed. AI-REGISTER-2 adds the
// attention strip and filter; it reports only and enforces nothing.

interface Unregistered {
  key: string
  calls: number
  cost_usd: number
  last_used_at: string | null
}

interface Summary {
  features: number
  needs_attention: number
  risk_not_set: number
  owner_missing: number
  review_overdue: number
  model_differs: number
  switched_off: number
  unregistered: number
}

interface RegisterReport {
  days: number
  features: RegisterFeature[]
  unregistered: Unregistered[]
  summary?: Summary
}

function errorText(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string }
  const detail = e?.response?.data?.detail
  return typeof detail === 'string' ? detail : e?.message || 'Save failed.'
}

export function AiRegisterSection() {
  const { data, loading, error, execute } = useApi<RegisterReport>()
  const [report, setReport] = useState<RegisterReport | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  const [attentionOnly, setAttentionOnly] = useState(false)
  const owners = useAiOwners()

  const load = useCallback(
    () => execute(() => apiClient.get<RegisterReport>('/v1/admin/ai-register').then(res => res.data)),
    [execute],
  )

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (data) setReport(data)
  }, [data])

  // Drafts are re-seeded only from a fresh report; a failed Save leaves them alone.
  useEffect(() => {
    if (!report) return
    const next: Record<string, Draft> = {}
    for (const f of report.features) next[f.key] = { owner: f.owner ?? '', risk: f.risk ?? '' }
    setDrafts(next)
  }, [report])

  const edit = (key: string, patch: Partial<Draft>) =>
    setDrafts(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))

  const save = async (key: string) => {
    const d = drafts[key]
    setRowErrors(prev => ({ ...prev, [key]: '' }))
    try {
      const res = await apiClient.put<RegisterReport>(`/v1/admin/ai-register/${key}`, {
        owner: d.owner.trim(),
        risk: d.risk === '' ? null : d.risk,
      })
      setReport(res.data)
    } catch (err) {
      setRowErrors(prev => ({ ...prev, [key]: errorText(err) }))
    }
  }

  const unregistered = report?.unregistered ?? []
  const summary = report?.summary
  const rows = (report?.features ?? []).filter(f => !attentionOnly || (f.attention ?? []).length > 0)

  return (
    <section data-testid="ai-register-section" className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">AI Register - last 30 days</h2>
      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading && !report && <p className="text-sm text-gray-500">Loading…</p>}

      {unregistered.length > 0 && (
        <div data-testid="register-unregistered" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="font-semibold">AI calls from features not on the register</p>
          <ul className="mt-1 space-y-1">
            {unregistered.map(u => (
              <li key={u.key}>
                <span className="font-mono">{u.key}</span> — {u.calls} calls, ${u.cost_usd.toFixed(4)}, last used {fmtDate(u.last_used_at)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary && (
        <p data-testid="register-summary" className="text-sm text-gray-700">
          {summary.features} features - {summary.needs_attention} need attention - {summary.risk_not_set} risk not set
          - {summary.owner_missing} owner missing - {summary.review_overdue} review overdue
        </p>
      )}
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" data-testid="register-filter-attention" checked={attentionOnly}
          onChange={e => setAttentionOnly(e.target.checked)} />
        Needs attention only
      </label>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-600">
            <th>Feature</th><th>Model(s)</th><th>Calls</th><th>Cost ($)</th>
            <th>Last used</th><th>Owner</th><th>Risk</th><th />
          </tr>
        </thead>
        <tbody>
          {rows.map(f => (
            <AiRegisterRow key={f.key} f={f}
              draft={drafts[f.key] ?? { owner: f.owner ?? '', risk: f.risk ?? '' }}
              error={rowErrors[f.key]} owners={owners} onEdit={edit} onSave={save} />
          ))}
        </tbody>
      </table>
    </section>
  )
}
